import { Link } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useState } from 'react';

interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  cluster: 'bom_pagador' | 'regular' | 'mau_pagador' | null;
  collection_rule_id?: string | null;
  created_at?: string;
}

interface ClienteRowProps {
  customer: Customer;
  onEdit: () => void;
  onDeleted: () => void;
}

// Format CPF/CNPJ
function maskTaxId(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

function getStatusBadge(cluster: string | null, createdAt?: string): { label: string; className: string } {
  // Check if customer is new (created in last 30 days)
  if (createdAt) {
    const created = new Date(createdAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    if (created > thirtyDaysAgo && cluster !== 'mau_pagador') {
      return { label: 'Novo', className: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]' };
    }
  }
  
  switch(cluster) {
    case 'mau_pagador':
      return { label: 'Inadimplente', className: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]' };
    case 'bom_pagador':
      return { label: 'Bom Pagador', className: 'bg-[hsl(142,70%,90%)] text-[hsl(142,70%,30%)]' };
    default:
      return { label: 'Regular', className: 'bg-muted text-muted-foreground' };
  }
}

export function ClienteRow({ customer, onEdit, onDeleted }: ClienteRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch collection rules for the rule name lookup
  const { data: collectionRules = [] } = useQuery({
    queryKey: ['collection-rules'],
    queryFn: async () => {
      const { data } = await supabase
        .from('collection_rules')
        .select('id, name');
      return data || [];
    },
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customer.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setConfirmOpen(false);
      onDeleted();
    }
  });

  const statusBadge = getStatusBadge(customer.cluster, customer.created_at);
  const ruleName = customer.collection_rule_id 
    ? (collectionRules.find(r => r.id === customer.collection_rule_id)?.name || 'Automática')
    : 'Sem régua';

  return (
    <>
      <Link 
        to={`/clientes/${customer.id}`}
        className="grid grid-cols-[2fr_1fr_8rem_1fr_8rem] gap-4 p-4 items-center hover:bg-[hsl(var(--basic-50))] transition-colors"
      >
        <span className="font-bold text-[hsl(var(--basic-900))] truncate">
          {customer.name}
        </span>
        <span className="text-muted-foreground font-mono text-sm">
          {maskTaxId(customer.tax_id)}
        </span>
        <span className="flex justify-center">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>
            {statusBadge.label}
          </span>
        </span>
        <span className="text-[hsl(var(--basic-600))] text-sm truncate">
          {ruleName}
        </span>
        <div className="flex justify-end gap-2" onClick={(e) => e.preventDefault()}>
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => {
              e.preventDefault();
              onEdit();
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.preventDefault();
              setConfirmOpen(true);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </Link>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Excluir Cliente"
        description="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita."
        onConfirm={() => deleteMutation.mutate()}
        confirmText="Excluir"
        variant="destructive"
      />
    </>
  );
}
